import {useCallback} from 'react';

import {GetActionFunctionsParams} from 'notebook/structured-tables/util/getActionFunctions';
import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {unmergeSingleInput} from 'notebook/tables/excess/ops/TableOps';
import toRanges from 'notebook/tables/excess/util/toRanges';
import {Table, TableData, TableOpFunction} from 'notebook/tables/TableConstants';

/** Returns a callback that unmerges the given single inputs in a given table.
 *
 * The callback returns the table with unmerged columns.
 */
export default function useUnmergeBrokenSingleInputsCallback(
  performTableOp: GetActionFunctionsParams<unknown, unknown>['performTableOp']
): (table: Table, brokenSingleInputIndexes: Array<number>) => Table {
  return useCallback(
    (table, brokenSingleInputIndexes) => {
      let unmergedTable = table;
      const tableOpFunc: TableOpFunction = (originalTableMap, computedTables, targetTableName) => {
        let tableMap: FullTableMap = {
          ...originalTableMap,
          [targetTableName]: {...originalTableMap[targetTableName], ...table},
        };
        const continuousRanges = toRanges(brokenSingleInputIndexes);
        for (const [start, end] of continuousRanges.reverse()) {
          tableMap = unmergeSingleInput(tableMap, computedTables, targetTableName, start, end).tables;
        }
        unmergedTable = tableMap[targetTableName];
        return {tables: tableMap, box: null};
      };
      performTableOp({op: tableOpFunc, options: {remakeSingleInputs: false}});
      return unmergedTable;
    },
    [performTableOp]
  );
}

/**
 * Returns the indexes of columns that are single inputs but have broken merge cells.
 * @param table
 * @returns A list of column indexes (in order)
 */
export function getBrokenSingleInputIndexes(table: TableData): Array<number> {
  const brokenSingleInputIndexes: Array<number> = [];
  for (const [columnIndex, column] of table.columnConstraints.entries()) {
    const {mergeRowSpan} = table.table[0][columnIndex];
    if (
      column?.structuredTableColumn?.isSingleInput &&
      (mergeRowSpan == null || mergeRowSpan !== table.table.length)
    ) {
      brokenSingleInputIndexes.push(columnIndex);
    }
  }
  return brokenSingleInputIndexes;
}
